import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  CREATE_UNSUPPORTED_POPUP,
  DELETE_UNSUPPORTED_POPUP,
  GET_UNSUPPORTED_POPUPS,
  UPDATE_UNSUPPORTED_POPUP,
} from './graphql';
import { normalizeVersion } from '../VersionControl/utils';

type LocaleTextMap = Record<string, string>;
type PopupTargetSystemName = 'IOS' | 'ANDROID';
type PopupType = 'REMINDER' | 'BLOCKER';
type PopupDisplayPolicy = 'EVERY_LAUNCH' | 'ONCE_PER_DAY';

type PopupConfig = {
  id: string;
  key: string;
  name: string;
  enabled: boolean;
  place: 'MAIN_MENU';
  targetSystemName: PopupTargetSystemName;
  popupType: PopupType;
  minimumDeviceVersion?: string | null;
  blockerReleaseVersion?: string | null;
  displayPolicy: PopupDisplayPolicy;
  titleTexts?: LocaleTextMap | null;
  bodyTexts?: LocaleTextMap | null;
};

const DEFAULT_TITLE_TEXTS: LocaleTextMap = {
  en: '{devicePlatform} Version\nUpdate Required',
  fr: 'Mise à jour requise\npour la version {devicePlatform}',
  de: 'Update für\n{devicePlatform}-Version erforderlich',
  it: 'Aggiornamento richiesto\nper la versione {devicePlatform}',
};

const DEFAULT_BODY_TEXTS: LocaleTextMap = {
  en:
    'We sincerely apologize for any inconvenience. To ensure a smooth experience and maintain the highest standards of stability and security, devices running below {version} will no longer be supported in the upcoming update.\n\n' +
    'Please update your device to {version} or later to continue your musical journey without interruption.\n\n' +
    'Rest assured, your game progress and account data will remain completely safe.',
  fr:
    'Nous vous prions sincèrement de nous excuser pour la gêne occasionnée. Afin de garantir une expérience fluide et de maintenir les plus hauts standards de stabilité et de sécurité, les appareils fonctionnant avec une version inférieure à {version} ne seront plus pris en charge dans notre prochaine mise à jour.\n\n' +
    'Veuillez mettre à jour votre appareil vers {version} ou une version ultérieure pour poursuivre votre aventure musicale sans interruption.\n\n' +
    'Rassurez-vous, votre progression et les données de votre compte resteront parfaitement en sécurité.',
  de:
    'Wir entschuldigen uns aufrichtig für etwaige Unannehmlichkeiten. Um ein reibungsloses Erlebnis zu gewährleisten und höchste Standards bei Stabilität und Sicherheit beizubehalten, werden Geräte mit einer Version unter {version} in unserem kommenden Update nicht mehr unterstützt.\n\n' +
    'Bitte aktualisieren Sie Ihr Gerät auf {version} oder höher, damit Sie Ihre musikalische Reise ohne Unterbrechung fortsetzen können.\n\n' +
    'Seien Sie versichert, dass Ihr Spielfortschritt und Ihre Kontodaten vollständig sicher bleiben.',
  it:
    "Ci scusiamo sinceramente per qualsiasi inconveniente. Per garantire un'esperienza fluida e mantenere i più alti standard di stabilità e sicurezza, i dispositivi con una versione inferiore a {version} non saranno più supportati nel nostro prossimo aggiornamento.\n\n" +
    'Ti invitiamo ad aggiornare il tuo dispositivo a {version} o versioni successive per continuare il tuo viaggio musicale senza interruzioni.\n\n' +
    'Stai tranquillo: i tuoi progressi di gioco e i dati del tuo account rimarranno completamente al sicuro.',
};

const formatJson = (value: LocaleTextMap | null | undefined) =>
  JSON.stringify(value ?? { en: '' }, null, 2);

const parseLocaleTextMap = (value: string, fieldName: string): LocaleTextMap => {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    throw new Error(`${fieldName} must be valid JSON.`);
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error(`${fieldName} must be a JSON object like {"en":"..."}.`);
  }

  const entries = Object.entries(parsedValue).filter(
    ([locale, text]) => typeof locale === 'string' && typeof text === 'string'
  );

  if (!entries.length) {
    throw new Error(`${fieldName} must contain at least one locale entry.`);
  }

  return Object.fromEntries(entries);
};

const readJsonFile = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      const result = event.target?.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file.'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const createDraftPopup = (
  platform: PopupTargetSystemName = 'IOS',
  popupType: PopupType = 'REMINDER'
): PopupConfig => {
  const name = `${platform === 'IOS' ? 'iOS' : 'Android'} ${popupType === 'REMINDER' ? 'Reminder' : 'Blocker'}`;
  const slug = slugify(name) || 'unsupported_popup';

  return {
    id: `unsupported-${slug}-${Date.now()}`,
    key: `unsupported_${slug}`,
    name,
    enabled: true,
    place: 'MAIN_MENU',
    targetSystemName: platform,
    popupType,
    minimumDeviceVersion: platform === 'IOS' ? '15.1' : '10.0',
    blockerReleaseVersion: '003.013.000',
    displayPolicy: popupType === 'BLOCKER' ? 'EVERY_LAUNCH' : 'ONCE_PER_DAY',
    titleTexts: DEFAULT_TITLE_TEXTS,
    bodyTexts: DEFAULT_BODY_TEXTS,
  };
};

const getPopupIdentity = (platform: PopupTargetSystemName, popupType: PopupType) => {
  const name = `${platform === 'IOS' ? 'iOS' : 'Android'} ${popupType === 'REMINDER' ? 'Reminder' : 'Blocker'}`;
  const slug = slugify(name) || 'unsupported_popup';

  return {
    name,
    key: `unsupported_${slug}`,
  };
};

const UnsupportedPopup = () => {
  const [selectedPopupId, setSelectedPopupId] = useState<string | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger' | 'info'>('info');
  const [isDraft, setIsDraft] = useState(false);
  const [id, setId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [targetSystemName, setTargetSystemName] = useState<PopupTargetSystemName>('IOS');
  const [popupType, setPopupType] = useState<PopupType>('REMINDER');
  const [minimumDeviceVersion, setMinimumDeviceVersion] = useState('15.1');
  const [blockerReleaseVersion, setBlockerReleaseVersion] = useState('003.013.000');
  const [displayPolicy, setDisplayPolicy] = useState<PopupDisplayPolicy>('ONCE_PER_DAY');
  const [titleTextsJson, setTitleTextsJson] = useState(formatJson(DEFAULT_TITLE_TEXTS));
  const [bodyTextsJson, setBodyTextsJson] = useState(formatJson(DEFAULT_BODY_TEXTS));

  const { data, loading, refetch } = useQuery(GET_UNSUPPORTED_POPUPS, {
    fetchPolicy: 'network-only',
  });
  const [createPopupConfig, { loading: createLoading }] = useMutation(CREATE_UNSUPPORTED_POPUP);
  const [updatePopupConfig, { loading: updateLoading }] = useMutation(UPDATE_UNSUPPORTED_POPUP);
  const [deletePopupConfig, { loading: deleteLoading }] = useMutation(DELETE_UNSUPPORTED_POPUP);

  const unsupportedPopups: PopupConfig[] = useMemo(() => {
    const configs = data?.liveOps?.unsupportedPopupConfigs ?? [];
    return [...configs].sort((left: PopupConfig, right: PopupConfig) => left.name.localeCompare(right.name));
  }, [data?.liveOps?.unsupportedPopupConfigs]);

  const selectedPopup = useMemo(
    () => unsupportedPopups.find((popup) => popup.id === selectedPopupId) ?? null,
    [selectedPopupId, unsupportedPopups]
  );

  useEffect(() => {
    if (isDraft) {
      return;
    }

    if (!selectedPopupId && unsupportedPopups.length > 0) {
      setSelectedPopupId(unsupportedPopups[0].id);
      return;
    }

    if (selectedPopupId && !selectedPopup) {
      setSelectedPopupId(unsupportedPopups[0]?.id ?? null);
    }
  }, [isDraft, selectedPopup, selectedPopupId, unsupportedPopups]);

  useEffect(() => {
    if (!selectedPopup) {
      return;
    }

    setId(selectedPopup.id);
    setEnabled(selectedPopup.enabled);
    setTargetSystemName(selectedPopup.targetSystemName);
    setPopupType(selectedPopup.popupType);
    setMinimumDeviceVersion(selectedPopup.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(selectedPopup.blockerReleaseVersion ?? '');
    setDisplayPolicy(selectedPopup.displayPolicy);
    setTitleTextsJson(formatJson(selectedPopup.titleTexts));
    setBodyTextsJson(formatJson(selectedPopup.bodyTexts));
    setIsDraft(false);
  }, [selectedPopup]);

  useEffect(() => {
    if (popupType === 'BLOCKER' && displayPolicy !== 'EVERY_LAUNCH') {
      setDisplayPolicy('EVERY_LAUNCH');
    }
  }, [displayPolicy, popupType]);

  const handleAddPopup = () => {
    const draft = createDraftPopup();
    setSelectedPopupId(draft.id);
    setId(draft.id);
    setEnabled(draft.enabled);
    setTargetSystemName(draft.targetSystemName);
    setPopupType(draft.popupType);
    setMinimumDeviceVersion(draft.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(draft.blockerReleaseVersion ?? '');
    setDisplayPolicy(draft.displayPolicy);
    setTitleTextsJson(formatJson(draft.titleTexts));
    setBodyTextsJson(formatJson(draft.bodyTexts));
    setIsDraft(true);
    setStatusMessage(null);
    setShowEditorModal(true);
  };

  const handleEditPopup = (popup: PopupConfig) => {
    setSelectedPopupId(popup.id);
    setId(popup.id);
    setEnabled(popup.enabled);
    setTargetSystemName(popup.targetSystemName);
    setPopupType(popup.popupType);
    setMinimumDeviceVersion(popup.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(popup.blockerReleaseVersion ?? '');
    setDisplayPolicy(popup.displayPolicy);
    setTitleTextsJson(formatJson(popup.titleTexts));
    setBodyTextsJson(formatJson(popup.bodyTexts));
    setIsDraft(false);
    setStatusMessage(null);
    setShowEditorModal(true);
  };

  const handleCloseEditor = () => {
    setShowEditorModal(false);
  };

  const handleJsonFileUpload =
    (setValue: (value: string) => void, fieldName: string) =>
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      try {
        const rawFile = await readJsonFile(file);
        const parsedMap = parseLocaleTextMap(rawFile, fieldName);
        setValue(formatJson(parsedMap));
        setStatusVariant('success');
        setStatusMessage(`${fieldName} loaded from ${file.name}.`);
      } catch (error) {
        setStatusVariant('danger');
        setStatusMessage(error instanceof Error ? error.message : `Failed to load ${fieldName}.`);
      }

      input.value = '';
    };

  const handleSave = async () => {
    try {
      const titleTexts = parseLocaleTextMap(titleTextsJson, 'Title LOC');
      const bodyTexts = parseLocaleTextMap(bodyTextsJson, 'Description LOC');
      const normalizedBlockerReleaseVersion = normalizeVersion(blockerReleaseVersion);
      const { key, name } = getPopupIdentity(targetSystemName, popupType);

      if (!minimumDeviceVersion.trim()) {
        throw new Error('Minimum device version is required.');
      }

      if (!normalizedBlockerReleaseVersion || normalizedBlockerReleaseVersion.length !== 11) {
        throw new Error('Version to block must use version format 003.013.000.');
      }

      const input = {
        id,
        key,
        name,
        enabled,
        place: 'MAIN_MENU',
        targetSystemName,
        popupType,
        minimumDeviceVersion: minimumDeviceVersion.trim(),
        blockerReleaseVersion: normalizedBlockerReleaseVersion,
        displayPolicy,
        titleTexts,
        bodyTexts,
      };

      if (isDraft) {
        await createPopupConfig({ variables: { input } });
      } else {
        await updatePopupConfig({ variables: { input } });
      }

      await refetch();
      setIsDraft(false);
      setShowEditorModal(false);
      setStatusVariant('success');
      setStatusMessage('Unsupported Pop Up saved.');
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Something went wrong :(');
    }
  };

  const handleDelete = async (popupId: string) => {
    if (!window.confirm('Delete this unsupported popup config?')) {
      return;
    }

    try {
      await deletePopupConfig({ variables: { id: popupId } });
      await refetch();
      setStatusVariant('success');
      setStatusMessage('Unsupported Pop Up deleted.');

      if (selectedPopupId === popupId) {
        setSelectedPopupId(null);
      }
    } catch {
      setStatusVariant('danger');
      setStatusMessage('Failed to delete unsupported popup.');
    }
  };

  const isSaving = createLoading || updateLoading;

  return (
    <div className="d-flex flex-column">
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <span className="font-weight-bold">
              <i className="fas fa-mobile-alt mr-2" />
              Unsupported Pop Up
            </span>
            {loading && <Spinner animation="border" size="sm" className="ml-2" />}
          </div>
          <Button variant="success" onClick={handleAddPopup}>
            <i className="fas fa-plus mr-2" />
            Add Pop Up
          </Button>
        </Card.Header>
        <Card.Body>
          {statusMessage ? (
            <Alert variant={statusVariant} className="py-2">
              {statusMessage}
            </Alert>
          ) : null}

          <Alert variant="info" className="py-2">
            Create one popup record per platform and popup type. Reminder records are shown before the blocker release.
            Blocker records are shown from the configured release onward. Use <code>{'{version}'}</code>,{' '}
            <code>{'{minimumDeviceVersion}'}</code>, and <code>{'{devicePlatform}'}</code> in localized copy.
          </Alert>

          <Table bordered size="sm" className="mb-4">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Type</th>
                <th>Title Preview</th>
                <th>Description Preview</th>
                <th>Min Device Version</th>
                <th>Version To Block</th>
                <th>Frequency</th>
                <th>Status</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unsupportedPopups.length ? (
                unsupportedPopups.map((popup) => (
                  <tr key={popup.id}>
                    <td>{popup.targetSystemName}</td>
                    <td>{popup.popupType}</td>
                    <td>{popup.titleTexts?.en || Object.values(popup.titleTexts ?? {})[0] || '-'}</td>
                    <td>{popup.bodyTexts?.en || Object.values(popup.bodyTexts ?? {})[0] || '-'}</td>
                    <td>{popup.minimumDeviceVersion || '-'}</td>
                    <td>{popup.blockerReleaseVersion || '-'}</td>
                    <td>{popup.displayPolicy}</td>
                    <td>
                      <Badge variant={popup.enabled ? 'success' : 'secondary'}>
                        {popup.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" className="mr-2" onClick={() => handleEditPopup(popup)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={deleteLoading}
                        onClick={() => handleDelete(popup.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No unsupported popups configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Modal show={showEditorModal} onHide={handleCloseEditor} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isDraft ? 'Add Unsupported Pop Up' : 'Edit Unsupported Pop Up'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mt-2">
            <Col xs={3}>Enabled</Col>
            <Col xs={9}>
              <Form.Check checked={enabled} onChange={(event) => setEnabled(event.currentTarget.checked)} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Device platform</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={targetSystemName}
                onChange={(event) =>
                  setTargetSystemName(event.currentTarget.value === 'ANDROID' ? 'ANDROID' : 'IOS')
                }
              >
                <option value="IOS">iOS</option>
                <option value="ANDROID">Android</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Popup type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={popupType}
                onChange={(event) =>
                  setPopupType(event.currentTarget.value === 'BLOCKER' ? 'BLOCKER' : 'REMINDER')
                }
              >
                <option value="REMINDER">Reminder</option>
                <option value="BLOCKER">Blocker</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Minimum device version</Col>
            <Col xs={6}>
              <Form.Control
                value={minimumDeviceVersion}
                onChange={(event) => setMinimumDeviceVersion(event.currentTarget.value)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Version to block</Col>
            <Col xs={6}>
              <Form.Control
                value={blockerReleaseVersion}
                onChange={(event) => setBlockerReleaseVersion(event.currentTarget.value)}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  setBlockerReleaseVersion(normalizeVersion(event.currentTarget.value))
                }
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Display frequency</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={displayPolicy}
                disabled={popupType === 'BLOCKER'}
                onChange={(event) =>
                  setDisplayPolicy(event.currentTarget.value === 'EVERY_LAUNCH' ? 'EVERY_LAUNCH' : 'ONCE_PER_DAY')
                }
              >
                <option value="EVERY_LAUNCH">Every launch</option>
                <option value="ONCE_PER_DAY">Every 24 hours</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Title LOC</Col>
            <Col xs={7}>
              <Form.Control
                as="textarea"
                rows={8}
                value={titleTextsJson}
                onChange={(event) => setTitleTextsJson(event.currentTarget.value)}
              />
            </Col>
            <Col xs={2}>
              <Form.File custom label="Browse" onChange={handleJsonFileUpload(setTitleTextsJson, 'Title LOC')} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Description LOC</Col>
            <Col xs={7}>
              <Form.Control
                as="textarea"
                rows={12}
                value={bodyTextsJson}
                onChange={(event) => setBodyTextsJson(event.currentTarget.value)}
              />
            </Col>
            <Col xs={2}>
              <Form.File custom label="Browse" onChange={handleJsonFileUpload(setBodyTextsJson, 'Description LOC')} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditor}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSave}>
            {isSaving ? 'Saving...' : isDraft ? 'Create Pop Up' : 'Save Pop Up'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UnsupportedPopup;
